import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { ChevronRight, Calendar, MapPin, Filter } from 'lucide-react';
import WCInternalLinks from '@/components/worldcup/WCInternalLinks';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { translateCountryName, wcMatchSlug } from '@/lib/wcTranslations';

const API_BASE = '';
const LOCALE_MAP = { tr: 'tr-TR', en: 'en-US', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', pt: 'pt-BR', it: 'it-IT', nl: 'nl-NL', ru: 'ru-RU', uk: 'uk-UA', ar: 'ar-SA', id: 'id-ID' };

export default function WorldCupFixtures() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roundFilter, setRoundFilter] = useState('');
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;

  useEffect(() => {
    const params = new URLSearchParams({ lang: currentLang });
    if (roundFilter) params.set('round', roundFilter);
    fetch(`${API_BASE}/api/worldcup/fixtures?${params}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [currentLang, roundFilter]);

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const { fixtures, rounds, seo } = data;
  const matchPath = currentLang === 'tr' ? 'mac' : 'match';

  // Group by date
  const byDate = {};
  fixtures.forEach(f => {
    const d = f.date?.slice(0, 10) || 'TBD';
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(f);
  });

  return (
    <div className="container mx-auto px-4 max-w-5xl py-6" data-testid="worldcup-fixtures">
      <WCSubmenu />
      <SEOHead title={seo?.title} description={seo?.description} canonical={seo?.canonical} hreflang={seo?.hreflang} jsonLd={seo?.jsonld} />

      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
        <Link to={`/${currentLang}`} className="hover:text-slate-300">{t('nav.home')}</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={wcBase} className="hover:text-amber-300 text-amber-400/70">Dünya Kupası 2026</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-300">Fikstür</span>
      </div>

      <h1 className="text-3xl md:text-4xl font-black text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
        <Calendar className="w-8 h-8 inline mr-3 text-amber-400" />FİKSTÜR
      </h1>
      <p className="text-sm text-slate-400 mb-6">{fixtures.length} maç - FIFA Dünya Kupası 2026</p>

      {/* Round Filter */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={() => setRoundFilter('')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${!roundFilter ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-slate-800/40 text-slate-500 hover:text-slate-300 border border-transparent'}`}>
          {t('wc.allFilter')}
        </button>
        {rounds.map(r => {
          const localRound = currentLang === 'tr'
            ? r.replace('Group Stage - ', 'Grup Aşaması - ').replace('Round of ', 'Son ')
            : r;
          return (
            <button key={r} onClick={() => setRoundFilter(r)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${roundFilter === r ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-slate-800/40 text-slate-500 hover:text-slate-300 border border-transparent'}`}>
              {localRound}
            </button>
          );
        })}
      </div>

      {/* Matches by Date */}
      {Object.entries(byDate).map(([date, matches]) => (
        <div key={date} className="mb-6">
          <div className="flex items-center gap-2 mb-2 px-1">
            <Calendar className="w-4 h-4 text-amber-400/60" />
            <h3 className="text-sm font-bold text-slate-300">
              {new Date(date).toLocaleDateString(LOCALE_MAP[currentLang] || 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h3>
            <span className="text-[10px] text-slate-600">({matches.length} {t('wc.matchesLabel', 'matches')})</span>
          </div>
          <div className="space-y-1">
            {matches.map(m => {
              const time = m.date ? new Date(m.date).toLocaleTimeString(LOCALE_MAP[currentLang] || 'en-US', { hour: '2-digit', minute: '2-digit' }) : '';
              const isTR = m.home_name === 'Türkiye' || m.away_name === 'Türkiye';
              return (
                <Link key={m.id} to={`${wcBase}/${matchPath}/${wcMatchSlug(m.home_name, m.away_name, m.id, currentLang)}`}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all hover:bg-slate-800/30 ${isTR ? 'border-red-500/20 bg-red-500/5' : 'border-white/5 bg-slate-900/30'}`}>
                  <span className="text-xs text-slate-500 w-12 text-center shrink-0">{time}</span>
                  <div className="flex items-center gap-2 flex-1">
                    <img src={m.home_logo} alt="" className="w-5 h-5" />
                    <span className={`text-sm ${m.home_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{translateCountryName(m.home_name, currentLang)}</span>
                  </div>
                  <span className="text-xs text-slate-600">vs</span>
                  <div className="flex items-center gap-2 flex-1 justify-end">
                    <span className={`text-sm ${m.away_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{translateCountryName(m.away_name, currentLang)}</span>
                    <img src={m.away_logo} alt="" className="w-5 h-5" />
                  </div>
                  <div className="text-right w-32 shrink-0">
                    <p className="text-[10px] text-slate-500">{m.venue_name}</p>
                    <p className="text-[9px] text-slate-600">{m.round.replace('Group Stage - ', t('wc.groupStagePrefix'))}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}

      <WCInternalLinks currentPage="fixtures" lang={currentLang} />
    </div>
  );
}
